/**
 * Mise en page caméras / contenu du live, partagée entre le compositor egress (`LkRecordingViewPage`,
 * la vérité vue par le spectateur) et l'aperçu régie (`LiveKitRoomContent`). Une seule géométrie,
 * exprimée en % du cadre 16:9, pour que la régie soit fidèle au flux quel que soit le cadre rendu
 * (1080/720/480/360 côté egress, aperçu réduit côté régie).
 *
 * Les valeurs px sont relevées sur la maquette Figma (frame 552×310,5) et converties pour un cadre
 * de référence 1920×1080 (facteur ×3,478).
 */

export const CAM_LAYOUT_FRAME = { width: 1920, height: 1080 } as const;

/** Options de disposition, numérotées comme le Figma (les options 1 et 3 ont été écartées). */
export type CamLayoutOption = 2 | 4 | 5;
export type CamFormat = 'VERTICAL' | 'RATIO_4_3' | 'RATIO_16_9';
export type CamSize = 'S' | 'M' | 'L';

export interface CamLayoutConfig {
  option: CamLayoutOption;
  format: CamFormat;
  size: CamSize;
}

/** Rectangle en % du cadre (0-100), x/y depuis le coin haut gauche. */
export interface PctRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface CamLayoutRects {
  /** Boîte du contenu principal, absente quand aucun contenu n'est présenté. */
  content: PctRect | null;
  /** Une vignette par présentateur affiché, dans l'ordre des présentateurs. */
  cams: PctRect[];
  /** Rayons en px du cadre de référence, à mettre à l'échelle du cadre rendu. */
  camRadiusPx: number;
  contentRadiusPx: number;
}

export const CAM_LAYOUT_OPTIONS: readonly CamLayoutOption[] = [2, 4, 5];
export const CAM_SIZES: readonly CamSize[] = ['L', 'M', 'S'];

/** Formats autorisés par option (maquette Figma). */
export const CAM_FORMATS_BY_OPTION: Record<CamLayoutOption, readonly CamFormat[]> = {
  2: ['VERTICAL', 'RATIO_4_3', 'RATIO_16_9'],
  4: ['RATIO_16_9', 'RATIO_4_3'],
  5: ['RATIO_4_3'],
};

export const DEFAULT_CAM_LAYOUT: CamLayoutConfig = { option: 2, format: 'RATIO_4_3', size: 'L' };

/** Disposition de repli quand l'option courante ne peut plus afficher tout le monde (RG8). */
const FALLBACK_OPTION: CamLayoutOption = 4;
const FALLBACK_FORMAT: CamFormat = 'RATIO_4_3';

/** Nombre maximal de présentateurs affichables sans contenu (grille). */
export const MAX_GRID_PRESENTERS = 9;

// Valeurs en px du cadre de référence 1920×1080.
const PADDING_PX = 35;
const GAP_PX = 28;
const CAM_RADIUS_PX = 14;
const CONTENT_RADIUS_PX = 42;
/** Hauteur d'une vignette (largeur pour le format vertical, même vignette tournée). */
const SIZE_PX: Record<CamSize, number> = { S: 170, M: 223, L: 292 };
const FORMAT_RATIO: Record<CamFormat, number> = {
  VERTICAL: 9 / 16,
  RATIO_4_3: 4 / 3,
  RATIO_16_9: 16 / 9,
};

interface PxRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

const { width: FW, height: FH } = CAM_LAYOUT_FRAME;

export function camTileDimsPx(format: CamFormat, size: CamSize): { w: number; h: number } {
  const base = SIZE_PX[size];
  return format === 'VERTICAL'
    ? { w: base, h: base / FORMAT_RATIO.VERTICAL }
    : { w: base * FORMAT_RATIO[format], h: base };
}

export function isValidCamLayout(config: CamLayoutConfig): boolean {
  return CAM_FORMATS_BY_OPTION[config.option]?.includes(config.format) ?? false;
}

const fitCount = (availablePx: number, tilePx: number): number =>
  Math.max(0, Math.floor((availablePx + GAP_PX) / (tilePx + GAP_PX)));

/** Nombre maximal de présentateurs que la disposition peut afficher avec du contenu. */
export function camLayoutCapacity(config: CamLayoutConfig): number {
  if (!isValidCamLayout(config)) return 0;
  const tile = camTileDimsPx(config.format, config.size);
  switch (config.option) {
    case 2:
      return fitCount(FH - 2 * PADDING_PX, tile.h);
    case 4:
      return fitCount(FW - 2 * PADDING_PX, tile.w);
    case 5:
      // Rangée collée au bord droit, on garde le padding à gauche.
      return fitCount(FW - PADDING_PX, tile.w);
  }
}

/** Plus grande taille (L > M > S) capable d'afficher `count` présentateurs, sinon null. */
export function largestCamSizeFor(
  option: CamLayoutOption,
  format: CamFormat,
  count: number,
): CamSize | null {
  return CAM_SIZES.find((size) => camLayoutCapacity({ option, format, size }) >= count) ?? null;
}

/**
 * Disposition à appliquer pour `count` présentateurs à partir de la disposition préférée (choix de
 * l'admin). Fonction pure : quand le nombre redescend, la préférence est restaurée (écart assumé
 * avec la RG9 du ticket, qui figeait la réduction ; à valider avec le produit).
 * - Si la préférence tient, elle est appliquée telle quelle.
 * - Sinon, même option et format, plus grande taille qui tient (RG7).
 * - Sinon, repli Option 4 / 4:3 à la plus grande taille qui tient (RG8).
 * - Au-delà de toute capacité, repli Option 4 / 4:3 / S : le rendu tronque à sa capacité (RG10).
 */
export function fitCamLayout(current: CamLayoutConfig, count: number): CamLayoutConfig {
  const base = isValidCamLayout(current) ? current : DEFAULT_CAM_LAYOUT;
  if (camLayoutCapacity(base) >= count) return base;
  const smaller = largestCamSizeFor(base.option, base.format, count);
  if (smaller) return { ...base, size: smaller };
  const fallbackSize = largestCamSizeFor(FALLBACK_OPTION, FALLBACK_FORMAT, count) ?? 'S';
  return { option: FALLBACK_OPTION, format: FALLBACK_FORMAT, size: fallbackSize };
}

const toPct = (r: PxRect): PctRect => ({
  x: (100 * r.x) / FW,
  y: (100 * r.y) / FH,
  w: (100 * r.w) / FW,
  h: (100 * r.h) / FH,
});

/** Boîte 16:9 la plus grande dans l'espace donné. */
function fit16by9(maxW: number, maxH: number): { w: number; h: number } {
  let w = maxW;
  let h = (w * 9) / 16;
  if (h > maxH) {
    h = maxH;
    w = (h * 16) / 9;
  }
  return { w, h };
}

function column(x: number, tile: { w: number; h: number }, count: number): PxRect[] {
  const total = count * tile.h + (count - 1) * GAP_PX;
  const y0 = (FH - total) / 2;
  return Array.from({ length: count }, (_, i) => ({
    x,
    y: y0 + i * (tile.h + GAP_PX),
    ...tile,
  }));
}

function topRow(y: number, tile: { w: number; h: number }, count: number): PxRect[] {
  const total = count * tile.w + (count - 1) * GAP_PX;
  const x0 = (FW - total) / 2;
  return Array.from({ length: count }, (_, i) => ({
    x: x0 + i * (tile.w + GAP_PX),
    y,
    ...tile,
  }));
}

function bottomRightRow(tile: { w: number; h: number }, count: number): PxRect[] {
  return Array.from({ length: count }, (_, i) => ({
    x: FW - (count - i) * tile.w - (count - i - 1) * GAP_PX,
    y: FH - tile.h,
    ...tile,
  }));
}

function withContentRects(config: CamLayoutConfig, count: number): CamLayoutRects {
  const tile = camTileDimsPx(config.format, config.size);
  const shown = Math.min(count, camLayoutCapacity(config));
  let content: PxRect;
  let cams: PxRect[];
  switch (config.option) {
    case 2: {
      const x = PADDING_PX + tile.w + PADDING_PX;
      const box = fit16by9(FW - x - PADDING_PX, FH - 2 * PADDING_PX);
      content = { x, y: (FH - box.h) / 2, ...box };
      cams = column(PADDING_PX, tile, shown);
      break;
    }
    case 4: {
      const y = PADDING_PX + tile.h + PADDING_PX;
      const box = fit16by9(FW - 2 * PADDING_PX, FH - y - PADDING_PX);
      content = { x: (FW - box.w) / 2, y, ...box };
      cams = topRow(PADDING_PX, tile, shown);
      break;
    }
    case 5: {
      content = { x: 0, y: 0, w: FW, h: FH };
      cams = bottomRightRow(tile, shown);
      break;
    }
  }
  return {
    content: toPct(content),
    cams: cams.map(toPct),
    camRadiusPx: CAM_RADIUS_PX,
    contentRadiusPx: config.option === 5 ? 0 : CONTENT_RADIUS_PX,
  };
}

/**
 * Grille sans contenu (maquette « Sans slides ») : le ratio des tuiles et la répartition par ligne
 * dépendent du nombre, les gaps sont ceux de la maquette convertis en 1080p. Dernière ligne centrée.
 */
const NO_CONTENT_GRID: Record<
  number,
  { ratio: number; rows: number[]; gap: number; rowGap?: number }
> = {
  1: { ratio: 16 / 9, rows: [1], gap: 0 },
  2: { ratio: 4 / 3, rows: [2], gap: 33 },
  3: { ratio: 16 / 9, rows: [2, 1], gap: 33, rowGap: 21 },
  4: { ratio: 16 / 9, rows: [2, 2], gap: 33, rowGap: 21 },
  5: { ratio: 4 / 3, rows: [3, 2], gap: 33 },
  6: { ratio: 4 / 3, rows: [3, 3], gap: 33 },
  7: { ratio: 16 / 9, rows: [3, 3, 1], gap: 17 },
  8: { ratio: 16 / 9, rows: [3, 3, 2], gap: 17 },
  9: { ratio: 16 / 9, rows: [3, 3, 3], gap: 17 },
};

function noContentRects(count: number): CamLayoutRects {
  const shown = Math.min(count, MAX_GRID_PRESENTERS);
  if (shown <= 0)
    return { content: null, cams: [], camRadiusPx: CAM_RADIUS_PX, contentRadiusPx: 0 };
  const { ratio, rows, gap, rowGap = gap } = NO_CONTENT_GRID[shown];
  const cols = Math.max(...rows);
  const availW = FW - 2 * PADDING_PX;
  const availH = FH - 2 * PADDING_PX;
  let w = (availW - (cols - 1) * gap) / cols;
  let h = w / ratio;
  const maxH = (availH - (rows.length - 1) * rowGap) / rows.length;
  if (h > maxH) {
    h = maxH;
    w = h * ratio;
  }
  const totalH = rows.length * h + (rows.length - 1) * rowGap;
  const y0 = (FH - totalH) / 2;
  const cams = rows.flatMap((rowCount, r) => {
    const totalW = rowCount * w + (rowCount - 1) * gap;
    const x0 = (FW - totalW) / 2;
    return Array.from({ length: rowCount }, (_, c) => ({
      x: x0 + c * (w + gap),
      y: y0 + r * (h + rowGap),
      w,
      h,
    }));
  });
  return { content: null, cams: cams.map(toPct), camRadiusPx: CAM_RADIUS_PX, contentRadiusPx: 0 };
}

/**
 * Rectangles à rendre pour `count` présentateurs. Avec contenu et aucun présentateur, le contenu
 * occupe tout le cadre sans angle arrondi (bord à bord).
 */
export function computeCamLayoutRects(
  config: CamLayoutConfig,
  count: number,
  hasContent: boolean,
): CamLayoutRects {
  if (!hasContent) return noContentRects(count);
  if (count <= 0) {
    return {
      content: { x: 0, y: 0, w: 100, h: 100 },
      cams: [],
      camRadiusPx: CAM_RADIUS_PX,
      contentRadiusPx: 0,
    };
  }
  return withContentRects(fitCamLayout(config, count), count);
}

/** Convertit une valeur px du cadre de référence vers la hauteur réelle du cadre rendu. */
export function scaleCamLayoutPx(px: number, frameHeightPx: number | null | undefined): number {
  if (!frameHeightPx || !Number.isFinite(frameHeightPx) || frameHeightPx <= 0) return px;
  return (px * frameHeightPx) / FH;
}

/** Style CSS d'un rect en %, à poser sur un enfant `absolute` du cadre. */
export function pctRectStyle(rect: PctRect): {
  left: string;
  top: string;
  width: string;
  height: string;
} {
  return {
    left: `${rect.x}%`,
    top: `${rect.y}%`,
    width: `${rect.w}%`,
    height: `${rect.h}%`,
  };
}

/** Lecture tolérante d'une config venue du réseau ou du stockage : null si invalide. */
export function parseCamLayoutConfig(value: unknown): CamLayoutConfig | null {
  if (!value || typeof value !== 'object') return null;
  const { option, format, size } = value as Record<string, unknown>;
  if (!CAM_LAYOUT_OPTIONS.includes(option as CamLayoutOption)) return null;
  if (!CAM_SIZES.includes(size as CamSize)) return null;
  const config = { option, format, size } as CamLayoutConfig;
  return isValidCamLayout(config) ? config : null;
}

// ---- Vocabulaire des réglages (back-office et régie) : Position + Format, tel que présenté à
// l'utilisateur. Les options 2/4/5 restent le modèle interne, dérivé de ce couple.

export type CamPosition = 'TOP' | 'LEFT' | 'BOTTOM_RIGHT';
/** Portrait = vertical à côté des slides (option 2), uniquement à gauche. */
export type CamFormatChoice = 'RATIO_16_9' | 'RATIO_4_3' | 'PORTRAIT';

export interface CamLayoutSelection {
  position: CamPosition;
  format: CamFormatChoice;
  size: CamSize;
}

export const CAM_POSITIONS: readonly CamPosition[] = ['TOP', 'LEFT', 'BOTTOM_RIGHT'];

export const CAM_FORMAT_CHOICES_BY_POSITION: Record<CamPosition, readonly CamFormatChoice[]> = {
  TOP: ['RATIO_16_9', 'RATIO_4_3'],
  LEFT: ['RATIO_16_9', 'RATIO_4_3', 'PORTRAIT'],
  BOTTOM_RIGHT: ['RATIO_4_3'],
};

export function toCamLayoutSelection(config: CamLayoutConfig): CamLayoutSelection {
  const { option, format, size } = config;
  if (option === 5) return { position: 'BOTTOM_RIGHT', format: 'RATIO_4_3', size };
  const choice: CamFormatChoice = format === 'VERTICAL' ? 'PORTRAIT' : format;
  return { position: option === 4 ? 'TOP' : 'LEFT', format: choice, size };
}

/** Convertit un choix utilisateur en config interne ; un format hors position retombe sur le premier permis. */
export function fromCamLayoutSelection(selection: CamLayoutSelection): CamLayoutConfig {
  const { position, size } = selection;
  const allowed = CAM_FORMAT_CHOICES_BY_POSITION[position];
  const format = allowed.includes(selection.format) ? selection.format : allowed[0];
  if (position === 'BOTTOM_RIGHT') return { option: 5, format: 'RATIO_4_3', size };
  if (format === 'PORTRAIT') return { option: 2, format: 'VERTICAL', size };
  return { option: position === 'TOP' ? 4 : 2, format, size };
}

// ---- Contrat API : le back porte la préférence sous la forme Position + Taille + Format, avec
// `PORTRAIT` là où le modèle interne dit `VERTICAL`. Toute la conversion vit ici.

export interface CamLayoutApiDto {
  position: CamPosition;
  size: CamSize;
  format: CamFormatChoice;
}

export function toApiCamLayout(config: CamLayoutConfig): CamLayoutApiDto {
  const { position, size, format } = toCamLayoutSelection(config);
  return { position, size, format };
}

/** Lecture tolérante d'une préférence venue de l'API ou de la metadata de room : null si invalide. */
export function fromApiCamLayout(value: unknown): CamLayoutConfig | null {
  if (!value || typeof value !== 'object') return null;
  const { position, size, format } = value as Record<string, unknown>;
  if (!CAM_POSITIONS.includes(position as CamPosition)) return null;
  if (!CAM_SIZES.includes(size as CamSize)) return null;
  const allowed = CAM_FORMAT_CHOICES_BY_POSITION[position as CamPosition];
  if (!allowed.includes(format as CamFormatChoice)) return null;
  return fromCamLayoutSelection({
    position: position as CamPosition,
    size: size as CamSize,
    format: format as CamFormatChoice,
  });
}

export const isSameCamLayout = (a: CamLayoutConfig, b: CamLayoutConfig): boolean =>
  a.option === b.option && a.format === b.format && a.size === b.size;

/**
 * Choix en attente après un nouvel état publié par la room. Tant qu'une écriture est en vol, une
 * metadata publiée pour une slide ou une vidéo porte encore l'ancienne préférence : lâcher le choix
 * ferait reculer la roue crantée avant qu'elle ne resaute. Une fois la dernière écriture acquittée,
 * la room fait foi, même si elle porte autre chose : sans ça, un évènement perdu, une reconnexion ou
 * une écriture concurrente gagnante laisse la régie sur un choix qui ne sera jamais confirmé.
 */
export function resolvePendingCamLayout(
  pending: CamLayoutConfig | null,
  fromRoom: CamLayoutConfig,
  hasWriteInFlight: boolean,
): CamLayoutConfig | null {
  if (!pending || isSameCamLayout(pending, fromRoom)) return null;
  return hasWriteInFlight ? pending : null;
}

/**
 * Choix réglé avant l'existence de la room (l'API l'a refusé, faute de room) : il est rejoué au
 * premier état publié, sauf si ce dernier le porte déjà.
 */
export function camLayoutToReplay(
  desired: CamLayoutConfig | null,
  fromRoom: CamLayoutConfig,
): CamLayoutConfig | null {
  return desired && !isSameCamLayout(desired, fromRoom) ? desired : null;
}
